import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link2, RefreshCw, CheckCircle2, AlertTriangle, Copy, Users, CalendarClock, MapPin, Plus, Trash2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SITE_LABEL } from "@/components/Layout";
import { XeroDeadLetterPanel } from "./XeroDeadLetterPanel";
import XeroResyncRangePanel from "./XeroResyncRangePanel";

function CopyField({ label, value, testid }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
        <div className="font-mono text-[12px] text-[#1A2744] break-all mt-0.5" data-testid={testid}>{value || "—"}</div>
      </div>
      {value && (
        <Button size="sm" variant="outline" onClick={copy} className="shrink-0" data-testid={`${testid}-copy`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export default function AdminXero() {
  const { user } = useAuth();
  const [state, setState] = useState({ connected: false });
  const [runs, setRuns] = useState([]);
  const [lastBulkRun, setLastBulkRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncProgress, setSyncProgress] = useState("");
  const [params, setParams] = useSearchParams();
  const [empSyncResult, setEmpSyncResult] = useState(null);

  const load = useCallback(async () => {
    const [s, h, lb] = await Promise.all([
      api.get("/admin/xero/status"),
      api.get("/admin/xero/history"),
      api.get("/admin/xero/last-bulk-leave-run").catch(() => ({ data: { last_run: null } })),
    ]);
    setState(s.data);
    setRuns(h.data.runs);
    setLastBulkRun(lb.data.last_run);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  // Handle redirect back from Xero callback
  useEffect(() => {
    const connected = params.get("xero");
    const err = params.get("xero_error");
    if (connected === "connected") {
      toast.success("Connected to Xero");
      params.delete("xero"); setParams(params, { replace: true });
      load().catch(() => {});
    } else if (err) {
      toast.error(`Xero connection failed: ${err}`);
      params.delete("xero_error"); params.delete("detail"); setParams(params, { replace: true });
    }
  }, [params, setParams, load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/xero/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Xero OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/admin/xero/disconnect"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    // The backend works to a wall-clock budget and tells us how much is left,
    // so one HTTP request always returns fast (no Cloudflare 520 on big
    // batches). Keep calling until nothing is deferred.
    let totalSynced = 0;
    let totalFailed = 0;
    let rounds = 0;
    const countdown = async (secs, left) => {
      for (let s = secs; s > 0; s -= 1) {
        setSyncProgress(
          `Xero is rate limiting · waiting ${s}s, then continuing` +
            (left ? ` · ${left} still queued` : "") + "…"
        );
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1000));
      }
    };
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        rounds += 1;
        let data;
        try {
          ({ data } = await api.post("/admin/xero/sync"));
        } catch (err) {
          // Xero throttled us before the batch even started — wait it out
          // rather than failing the whole run; nothing has been lost.
          if (err?.response?.status === 429 && rounds < 40) {
            await countdown(20, 0);
            continue;
          }
          throw err;
        }
        totalSynced += data.run.records_synced || 0;
        totalFailed += data.run.records_failed || 0;
        const left = data.remaining || 0;
        // Daily cap only resets at midnight UTC — retrying is pointless.
        if (data.rate_limit_problem === "daily") {
          toast.warning(
            data.message ||
              "Xero's daily API limit has been reached. The queued shifts stay safe and will sync after it resets at midnight UTC (1pm NZT)."
          );
          load();
          return;
        }
        if (left > 0 && rounds < 40) {
          if (data.rate_limited) {
            await countdown(Math.min(data.retry_after_sec || 20, 60), left);
          } else {
            setSyncProgress(`Synced ${totalSynced} so far · ${left} still queued…`);
          }
          load();
          continue;
        }
        if (left > 0) {
          toast.warning(
            `Synced ${totalSynced}. ${left} still queued — Xero is throttling us, run Sync now again in a few minutes.`
          );
          load();
          return;
        }
        break;
      }
      if (totalFailed > 0) {
        toast.warning(`Synced ${totalSynced}, ${totalFailed} failed. See sync history.`);
      } else {
        toast.success(`Synced ${totalSynced} records`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); setSyncProgress(""); }
  };

  const syncEmployees = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/xero/sync-employees");
      setEmpSyncResult(data);
      const sc = data.site_changed?.length || 0;
      const seen = data.seen_holiday_keys?.length || 0;
      const parts = [
        `${data.created.length} created`,
        `${data.linked.length} linked`,
        `${data.skipped.length} skipped`,
      ];
      if (sc > 0) parts.push(`${sc} site moves`);
      if (data.contracted_hours_synced > 0) parts.push(`${data.contracted_hours_synced} contracted hours`);
      if (seen > 0) parts.push(`${seen} Holiday Group keys observed`);
      toast.success(`Xero employees: ${parts.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Employee sync failed");
    } finally { setBusy(false); }
  };

  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [rawBusy, setRawBusy] = useState(false);
  const [raw, setRaw] = useState(null);
  const runDiagnostic = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.get("/admin/xero/debug/employees");
      setDiag(data);
      const s = data.summary;
      toast.success(
        `Xero has ${s.total_in_xero} · ${s.would_create} would create, ${s.linked} linked, ${s.would_skip} would skip`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagnostic failed");
    } finally { setDiagBusy(false); }
  };
  const showRawResponse = async () => {
    setRawBusy(true);
    try {
      const { data } = await api.get("/admin/xero/debug/raw-response");
      setRaw(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Raw fetch failed");
    } finally { setRawBusy(false); }
  };

  const toggleAutoSync = async (next) => {
    if (user?.role !== "SUPERADMIN") {
      toast.error("Only super-admins can change Xero auto-sync.");
      return;
    }
    // Optimistic update
    setState((s) => ({ ...s, auto_sync_leave_on_approve: next }));
    try {
      await api.patch("/admin/xero/settings", { auto_sync_leave_on_approve: next });
      toast.success(next ? "Auto-sync ON · new approvals will push to Xero" : "Auto-sync OFF · approvals will NOT push to Xero");
    } catch (e) {
      // rollback
      setState((s) => ({ ...s, auto_sync_leave_on_approve: !next }));
      toast.error(e?.response?.data?.detail || "Could not update setting");
    }
  };

  const [leaveTypes, setLeaveTypes] = useState(null);
  const [ltDraft, setLtDraft] = useState({});
  const [ltBusy, setLtBusy] = useState(false);

  const loadLeaveTypes = async () => {
    setLtBusy(true);
    try {
      const { data } = await api.get("/admin/xero/leave-types");
      setLeaveTypes(data);
      const seed = {};
      data.mappings.forEach((m) => { seed[m.code] = m.mapped_to || ""; });
      setLtDraft(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Xero leave types");
    } finally { setLtBusy(false); }
  };

  const saveLeaveTypeMap = async () => {
    setLtBusy(true);
    try {
      const { data } = await api.put("/admin/xero/leave-type-map", { leave_type_map: ltDraft });
      toast.success(
        `Mapping saved${data.requeued ? ` · ${data.requeued} leave row${data.requeued === 1 ? "" : "s"} re-queued` : ""}`
      );
      await loadLeaveTypes();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save mapping");
    } finally { setLtBusy(false); }
  };

  const bulkSyncLeave = async () => {    setBusy(true);
    try {
      const { data } = await api.post("/admin/xero/sync-leave/bulk");
      const { attempted, synced, skipped, failed } = data;
      if (attempted === 0) {
        toast.message("Nothing to sync", { description: "No approved leave is awaiting Xero." });
      } else if (failed.length === 0 && skipped.length === 0) {
        toast.success(`Synced ${synced.length} approved leave to Xero`);
      } else {
        toast.warning(
          `${synced.length} synced · ${skipped.length} skipped · ${failed.length} failed`,
          { description: failed[0]?.error?.slice(0, 120) || skipped[0]?.status || "See Admin · Leave for per-row details." },
        );
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk leave sync failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-xero-page">
      <header>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Xero payroll sync</h1>
        <p className="text-gray-500 mt-1">Push approved shifts and leave to Xero Payroll NZ. Tokens are encrypted at rest.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Connection</div>
                <div className="mt-2 flex items-center gap-2">
                  {state.connected
                    ? <><CheckCircle2 className="h-5 w-5 text-emerald-500" /> <span className="font-heading text-xl font-bold text-[#1A2744]" data-testid="xero-connected-label">Connected</span></>
                    : <><AlertTriangle className="h-5 w-5 text-amber-500" /> <span className="font-heading text-xl font-bold text-gray-500" data-testid="xero-disconnected-label">Not connected</span></>}
                </div>
                {state.connection && (
                  <div className="text-xs text-gray-500 mt-1">
                    Tenant: <span className="font-mono">{state.connection.tenant_name}</span> · connected {new Date(state.connection.connected_at).toLocaleString()}
                  </div>
                )}
                <div className="mt-3 text-xs text-[#C9A84C] font-semibold">{state.scopes?.length ? `${state.scopes.length} scopes` : ""}</div>
              </div>
              <div className="flex flex-col gap-2">
                {!state.connected && user?.role === "SUPERADMIN" && (
                  <Button onClick={connect} disabled={busy} className="bg-[#1A2744] text-white" data-testid="xero-connect">
                    <Link2 className="h-4 w-4 mr-1" /> Connect Xero
                  </Button>
                )}
                {state.connected && user?.role === "SUPERADMIN" && (
                  <Button onClick={disconnect} disabled={busy} variant="outline" data-testid="xero-disconnect">Disconnect</Button>
                )}
                <Button onClick={sync} disabled={busy || !state.connected} className="bg-[#C9A84C] hover:bg-[#E6C568] text-[#1A2744] font-semibold" data-testid="xero-sync-now">
                  <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Sync now
                </Button>
                {syncProgress && (
                  <p className="text-xs text-[#1A2744]/70 text-center" data-testid="xero-sync-progress">{syncProgress}</p>
                )}
                <Button onClick={syncEmployees} disabled={busy || !state.connected} variant="outline" data-testid="xero-sync-employees" title="Pulls employees, their site mapping and their contracted hours from Xero">
                  <Users className="h-4 w-4 mr-1" /> Sync employees
                </Button>
                <Button onClick={runDiagnostic} disabled={diagBusy || !state.connected} variant="outline" data-testid="xero-diagnose-employees" title="Show every Xero employee and how the sync will classify them — useful when employees in Xero don't appear on the portal">
                  <AlertTriangle className="h-4 w-4 mr-1" /> {diagBusy ? "Scanning…" : "Diagnose"}
                </Button>
                <Button onClick={showRawResponse} disabled={rawBusy || !state.connected} variant="outline" data-testid="xero-raw-response" title="Show the raw first-page JSON shape — confirms whether Xero is returning data and what field casing it's using">
                  <FileText className="h-4 w-4 mr-1" /> {rawBusy ? "Fetching…" : "Raw response"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Pending to sync</div>
            <div className="font-heading text-4xl font-bold text-[#1A2744] mt-2" data-testid="xero-pending">{state.pending_shifts ?? 0}</div>
            <div className="text-xs text-gray-500">approved shifts awaiting sync</div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="font-heading text-2xl font-bold text-[#1A2744]" data-testid="xero-pending-leave">{state.pending_leave ?? 0}</div>
              <div className="text-xs text-gray-500">approved leave awaiting sync</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-gray-200" data-testid="xero-leave-sync-card">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <CalendarClock className="h-5 w-5 text-[#C9A84C] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-heading text-lg font-bold text-[#1A2744]">Leave auto-sync</div>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">
                  When ON, approving leave automatically pushes a Xero LeaveApplication.
                  When OFF, approvals are flagged <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">AUTO_SYNC_DISABLED</span> and you push them in batch via the button below — handy for monthly payroll runs.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-gray-500">Off</span>
              <Switch
                checked={!!state.auto_sync_leave_on_approve}
                onCheckedChange={toggleAutoSync}
                disabled={busy || user?.role !== "SUPERADMIN"}
                data-testid="xero-auto-sync-toggle"
              />
              <span className="text-xs text-gray-500">On</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              <span className="font-semibold text-[#1A2744]">{state.pending_leave ?? 0}</span> approved leave row{(state.pending_leave ?? 0) === 1 ? "" : "s"} awaiting Xero. Bulk sync bypasses the toggle and force-pushes everything pending.
            </div>
            <Button
              onClick={bulkSyncLeave}
              disabled={busy || !state.connected || (state.pending_leave ?? 0) === 0}
              className="bg-[#1A2744] hover:bg-[#1A2744]/90 text-white"
              data-testid="xero-bulk-sync-leave"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />
              Sync all approved leave now
            </Button>
          </div>
          {user?.role !== "SUPERADMIN" && (
            <p className="text-[11px] text-amber-600 mt-2">Only super-admins can change the auto-sync toggle. Bulk sync is admin-allowed.</p>
          )}
          {lastBulkRun ? (
            <div className="mt-4 pt-4 border-t border-gray-100" data-testid="xero-last-bulk-run">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Last bulk run</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {new Date(lastBulkRun.ts).toLocaleString()}
                    {lastBulkRun.by ? <> · by <span className="text-[#1A2744] font-medium">{lastBulkRun.by}</span></> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="xero-last-bulk-synced">
                    {lastBulkRun.synced} synced
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="xero-last-bulk-skipped">
                    {lastBulkRun.skipped} skipped
                  </span>
                  <span className={`px-2 py-0.5 rounded-full border ${lastBulkRun.failed > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-gray-50 text-gray-500 border-gray-200"}`} data-testid="xero-last-bulk-failed">
                    {lastBulkRun.failed} failed
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={bulkSyncLeave}
                    disabled={busy || !state.connected}
                    className="h-7 text-[11px]"
                    data-testid="xero-rerun-bulk"
                    title="Re-run with the same scope"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} />
                    Re-run
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-gray-200" data-testid="xero-leave-type-map-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <CalendarClock className="h-5 w-5 text-[#C9A84C] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-lg font-bold text-[#1A2744]">Leave type mapping</div>
                <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                  Match each of your leave types to the one in Xero. Leave rows that
                  can&apos;t be matched are parked as{" "}
                  <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">SKIPPED_NO_TYPE_MATCH</span>{" "}
                  instead of syncing. Saving re-queues those rows automatically.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={loadLeaveTypes}
                disabled={ltBusy || !state.connected}
                className="shrink-0 min-h-[44px] sm:min-h-0"
                data-testid="xero-load-leave-types"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${ltBusy ? "animate-spin" : ""}`} />
                {leaveTypes ? "Refresh" : "Load from Xero"}
              </Button>
            </div>

            {leaveTypes ? (
              <div className="mt-5 space-y-3">
                {leaveTypes.mappings.map((m) => {
                  const current = ltDraft[m.code] ?? m.mapped_to ?? "";
                  const autoName = leaveTypes.xero_leave_types.find((x) => x.id === m.auto_match)?.name;
                  return (
                    <div
                      key={m.code}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pb-3 border-b last:border-b-0 border-gray-100"
                      data-testid={`leave-type-row-${m.code}`}
                    >
                      <div className="sm:w-56 shrink-0">
                        <div className="text-sm font-medium text-[#1A2744]">{m.label}</div>
                        {!current && autoName ? (
                          <div className="text-[11px] text-gray-500">Auto-matching to “{autoName}”</div>
                        ) : null}
                        {!current && !autoName ? (
                          <div className="text-[11px] text-amber-600">No match — leave won&apos;t sync</div>
                        ) : null}
                      </div>
                      <Select
                        value={current || "__none__"}
                        onValueChange={(v) =>
                          setLtDraft((d) => ({ ...d, [m.code]: v === "__none__" ? "" : v }))
                        }
                      >
                        <SelectTrigger
                          className="flex-1 min-h-[44px]"
                          data-testid={`leave-type-select-${m.code}`}
                        >
                          <SelectValue placeholder="-- not mapped --" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- not mapped --</SelectItem>
                          {leaveTypes.xero_leave_types.map((x) => (
                            <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={saveLeaveTypeMap}
                    disabled={ltBusy}
                    className="bg-[#1A2744] hover:bg-[#1A2744]/90 text-white min-h-[44px]"
                    data-testid="xero-save-leave-type-map"
                  >
                    Save mapping
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-4" data-testid="leave-type-map-empty">
                {state.connected
                  ? "Load your Xero leave types to set the mapping."
                  : "Connect Xero first, then load your leave types here."}
              </p>
            )}
          </CardContent>
        </Card>


      {/* Stuck timesheet sync — dead-letter inbox */}
      <XeroDeadLetterPanel canSync={state.connected && !busy} />

      {/* Manual re-sync — fix wrongly-dated or edited shifts already in Xero */}
      <XeroResyncRangePanel canSync={state.connected && !busy} />

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">Xero app registration</h3>
            <p className="text-xs text-gray-500 mt-1">
              Paste these exact values into <span className="font-medium">developer.xero.com → My Apps → your app</span>. Multiple redirect URIs are allowed — add your production domain alongside the preview.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CopyField
              label="Company / application URL"
              value={typeof window !== "undefined" ? window.location.origin : ""}
              testid="xero-app-url"
            />
            <CopyField
              label="OAuth 2.0 redirect URI"
              value={state.redirect_uri}
              testid="xero-redirect-uri"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Required scopes</div>
            <div className="flex flex-wrap gap-2" data-testid="xero-scopes-list">
              {(state.scopes || []).map((s) => (
                <span
                  key={s}
                  className="px-2.5 py-1 rounded-full bg-[#1A2744]/5 border border-[#1A2744]/10 text-[11px] font-mono text-[#1A2744]"
                  data-testid={`xero-scope-${s.replace(/\./g, "-")}`}
                >
                  {s}
                </span>
              ))}
            </div>
            {state.scopes?.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(state.scopes.join(" "));
                    toast.success("Scopes copied (space-separated)");
                  } catch { toast.error("Copy failed"); }
                }}
                data-testid="xero-scopes-copy-all"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy all scopes
              </Button>
            )}
          </div>
          <div className="text-[11px] text-gray-500 border-t border-gray-100 pt-3">
            Client ID configured on server:{" "}
            <span className={state.client_id_configured ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"} data-testid="xero-client-id-status">
              {state.client_id_configured ? "Yes" : "Not yet"}
            </span>
            . Set <code className="bg-gray-100 px-1 rounded">XERO_CLIENT_ID</code>, <code className="bg-gray-100 px-1 rounded">XERO_CLIENT_SECRET</code>, <code className="bg-gray-100 px-1 rounded">ENCRYPTION_KEY</code> in <code className="bg-gray-100 px-1 rounded">/app/backend/.env</code> to wire real Xero.
          </div>
        </CardContent>
      </Card>

      {raw && (
        <Card data-testid="xero-raw-card">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#1A2744]" />
                  <h3 className="font-heading text-lg font-bold text-[#1A2744]">Raw Xero response</h3>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  First page of <code>GET /Employees</code> · HTTP <strong>{raw.http_status}</strong>
                  {" · "}{raw.employees_count_on_page} employee{raw.employees_count_on_page === 1 ? "" : "s"} on this page
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(JSON.stringify(raw, null, 2)); toast.success("Copied"); }}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy JSON
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRaw(null)}>Close</Button>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Top-level keys (with types)</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(raw.top_level_keys || {}).map(([k, t]) => (
                  <span key={k} className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-700">{k}: <span className="text-gray-500">{t}</span></span>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Look for <code>employees</code> (camelCase, NZ Payroll) or <code>Employees</code> (PascalCase, AU / older).
              </p>
            </div>

            {raw.first_employee_keys?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">First employee — keys present</div>
                <div className="flex flex-wrap gap-1.5">
                  {raw.first_employee_keys.map((k) => (
                    <span key={k} className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900">{k}</span>
                  ))}
                </div>
              </div>
            )}

            {raw.first_employee_raw && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">First employee — raw JSON</div>
                <pre className="text-[11px] font-mono bg-[#0F1A30] text-[#E8ECFF] rounded p-3 overflow-auto max-h-[420px]">
{JSON.stringify(raw.first_employee_raw, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {diag && (
        <Card data-testid="xero-diagnostic-card">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <h3 className="font-heading text-lg font-bold text-[#1A2744]">Xero employee diagnostic</h3>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Xero returned <strong>{diag.summary.total_in_xero}</strong> employees ·{" "}
                  <span className="text-emerald-700">{diag.summary.would_create} would be created</span> ·{" "}
                  <span className="text-blue-700">{diag.summary.linked} already linked</span>{diag.summary.linked_inactive > 0 && <> · <span className="text-gray-500">{diag.summary.linked_inactive} linked but inactive</span></>} ·{" "}
                  <span className="text-amber-700">{diag.summary.would_skip} would be skipped</span>
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDiag(null)}>Close</Button>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[170px]">Classification</TableHead>
                    <TableHead>Why / current state</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diag.rows.map((r) => {
                    const klass = r.classification;
                    const pill =
                      klass === "WOULD_CREATE" ? "bg-emerald-100 text-emerald-800" :
                      klass === "LINKED" ? "bg-blue-100 text-blue-800" :
                      klass === "LINKED_INACTIVE" ? "bg-gray-200 text-gray-700" :
                      "bg-amber-100 text-amber-800";
                    return (
                      <TableRow key={r.xero_id || r.email || r.name}>
                        <TableCell className="text-sm">{r.name}</TableCell>
                        <TableCell className="text-xs font-mono text-gray-600">{r.email || "—"}</TableCell>
                        <TableCell className="text-xs">{r.status || "—"}</TableCell>
                        <TableCell>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${pill}`}>{klass}</span>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {r.reason && <div className="text-amber-700">{r.reason}</div>}
                          {r.existing_user_id && (
                            <div className="font-mono text-[11px]">
                              {r.existing_employee_code} · {SITE_LABEL[r.existing_primary_site] || r.existing_primary_site}
                            </div>
                          )}
                          {r.classification === "WOULD_CREATE" && (
                            <div className="text-emerald-700">
                              site → {SITE_LABEL[r.site_resolved] || r.site_resolved}
                              {r.holiday_keys?.length > 0 && <span className="ml-2 text-gray-400">via [{r.holiday_keys.join(", ")}]</span>}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <SiteMappingCard />

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">Sync history</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Attempted</TableHead>
                <TableHead>Synced</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No syncs yet.</TableCell></TableRow>}
              {runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-gray-500">{new Date(r.started_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-gray-500">{r.completed_at ? new Date(r.completed_at).toLocaleString() : "—"}</TableCell>
                  <TableCell>{r.triggered_by_name}</TableCell>
                  <TableCell>{r.records_attempted}</TableCell>
                  <TableCell className="text-emerald-600 font-semibold">{r.records_synced}</TableCell>
                  <TableCell className={r.records_failed ? "text-red-600 font-semibold" : ""}>
                    {r.records_failed}
                    {r.errors?.length > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] text-red-600 underline">errors</summary>
                        <ul className="mt-1 text-[10px] text-gray-600 space-y-0.5 max-w-md">
                          {r.errors.map((err, idx) => <li key={`${idx}-${(err || '').slice(0, 50)}`} className="break-all">{err}</li>)}
                        </ul>
                      </details>
                    )}
                  </TableCell>
                  <TableCell><StatusPill status={r.status === "COMPLETED" ? "SYNCED" : r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!empSyncResult} onOpenChange={(v) => { if (!v) setEmpSyncResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Xero employee sync results</DialogTitle>
          </DialogHeader>
          {empSyncResult && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Created</div>
                  <div className="font-heading text-3xl font-bold text-emerald-700">{empSyncResult.created.length}</div>
                </div>
                <div className="p-3 rounded-lg bg-[#1A2744]/5 border border-[#1A2744]/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#1A2744] font-semibold">Linked</div>
                  <div className="font-heading text-3xl font-bold text-[#1A2744]">{empSyncResult.linked.length}</div>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Skipped</div>
                  <div className="font-heading text-3xl font-bold text-amber-700">{empSyncResult.skipped.length}</div>
                </div>
              </div>

              {empSyncResult.created.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">New employees — share these PINs</div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Name</th>
                          <th className="px-3 py-2 text-left font-medium">Code</th>
                          <th className="px-3 py-2 text-left font-medium">PIN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empSyncResult.created.map((u) => (
                          <tr key={u.id} className="border-t border-gray-100">
                            <td className="px-3 py-2">{u.name}<div className="text-[10px] text-gray-500">{u.email}</div></td>
                            <td className="px-3 py-2 font-mono">{u.employee_code}</td>
                            <td className="px-3 py-2 font-mono text-[#1A2744] font-bold tracking-widest">{u.pin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-amber-700 mt-2">
                    Write these down or copy them now — PINs can be re-generated later via Reset PIN but not retrieved.
                  </p>
                </div>
              )}

              {empSyncResult.linked.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Matched to existing employees (xero_employee_id set)</div>
                  <ul className="text-xs text-gray-600 space-y-0.5">
                    {empSyncResult.linked.map((u) => <li key={u.id}>{u.name} · {u.email}</li>)}
                  </ul>
                </div>
              )}

              {empSyncResult.skipped.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Skipped</div>
                  <ul className="text-xs text-gray-600 space-y-0.5">
                    {empSyncResult.skipped.map((u, i) => <li key={u.id || `${u.name}-${i}`}>{u.name} — <span className="text-amber-700">{u.reason}</span></li>)}
                  </ul>
                </div>
              )}

              {empSyncResult.site_changed?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">
                    Site changed by Holiday Group ({empSyncResult.site_changed.length})
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {empSyncResult.site_changed.map((u) => (
                      <li key={u.id}>
                        {u.name} —{" "}
                        <span className="font-mono text-gray-500">{u.from || "—"}</span>
                        {" → "}
                        <span className="font-semibold text-[#1A2744]">{u.to}</span>
                        {u.matched_key && <span className="text-gray-400 ml-2">via &quot;{u.matched_key}&quot;</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {empSyncResult.seen_holiday_keys?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 font-semibold">
                    Holiday Group / Calendar / Location keys observed in this tenant ({empSyncResult.seen_holiday_keys.length})
                  </summary>
                  <ul className="mt-1 ml-4 font-mono text-[11px] text-gray-500">
                    {empSyncResult.seen_holiday_keys.map((k) => <li key={k}>{k}</li>)}
                  </ul>
                  <p className="mt-2 text-gray-500">
                    Map any of these to a site below in <span className="font-semibold">Holiday Group → Site mapping</span>.
                  </p>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ───────────────────────────────────────────────────────────────────────────
// Site mapping — assign Xero Holiday-Group / Calendar / Location keys to
// portal sites so the next Xero sync routes each employee to the correct
// site automatically.
// ───────────────────────────────────────────────────────────────────────────
function SiteMappingCard() {
  const [data, setData] = useState({ mapping: {}, seen_keys: [], available_sites: [] });
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [newKey, setNewKey] = useState("");
  const [newSite, setNewSite] = useState("");
  const [saving, setSaving] = useState(false);
  const [fixBusy, setFixBusy] = useState(false);
  const [fixPreview, setFixPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/xero/site-mapping");
      setData(d);
      setDraft({ ...(d.mapping || {}) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load mapping");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Default the "Add new mapping" site picker to the first available site
  // once they've loaded. Derived from data — no effect needed.
  const newSiteValue = newSite || data.available_sites?.[0] || "";

  const allKeys = Array.from(new Set([...(data.seen_keys || []), ...Object.keys(draft)])).sort();

  const setMappingFor = (key, site) => {
    setDraft((d) => {
      const next = { ...d };
      if (!site) { delete next[key]; } else { next[key] = site; }
      return next;
    });
  };

  const addRow = () => {
    const k = newKey.trim().toLowerCase();
    const s = (newSiteValue || "").trim().toUpperCase();
    if (!k || !s) { toast.error("Key and site are both required"); return; }
    if (draft[k]) { toast.error("Key already mapped — edit the existing row instead"); return; }
    setDraft({ ...draft, [k]: s });
    setNewKey("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/xero/site-mapping", { mapping: draft });
      const count = Object.keys(d.mapping).length;
      toast.success(`Saved ${count} mapping${count === 1 ? "" : "s"}`);
      setData({ ...data, mapping: d.mapping });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.mapping || {});

  const runFix = async (dryRun) => {
    setFixBusy(true);
    try {
      const { data: d } = await api.post(`/admin/users/fix-employee-codes?dry_run=${dryRun}`);
      if (dryRun) {
        setFixPreview(d);
      } else {
        toast.success(`Reassigned codes for ${d.fixed_count} employee${d.fixed_count === 1 ? "" : "s"}`);
        setFixPreview(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fix failed");
    } finally {
      setFixBusy(false);
    }
  };

  return (
    <Card data-testid="xero-site-mapping-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-[#C9A84C]/15"><MapPin className="h-4 w-4 text-[#1A2744]" /></div>
              <h3 className="font-heading text-lg font-bold text-[#1A2744]">Holiday Group &rarr; Site mapping</h3>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              When syncing employees from Xero, the portal looks at each employee&apos;s Holiday Group,
              Payroll Calendar, and Location ID / Name. Any value listed here will route the employee to
              the configured site on every future sync &mdash; overriding the address-based fallback heuristic.
              Manual edits on individual employees stay intact until a Xero key here explicitly maps them
              somewhere else.
            </p>
          </div>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#1A2744] hover:bg-[#0F1A30]"
            data-testid="save-site-mapping"
          >
            {saving ? "Saving..." : dirty ? "Save mapping" : "Saved"}
          </Button>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading...</div>}

        {!loading && (
          <>
            {allKeys.length === 0 ? (
              <div className="text-sm text-gray-500 italic py-4 px-3 bg-amber-50 border border-amber-200 rounded">
                No Holiday Groups / Payroll Calendars detected yet. Run a Xero employee sync first &mdash;
                the portal will then surface every observed key here for you to map.
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Xero key (Holiday Group / Calendar / Location ID or name)</TableHead>
                      <TableHead className="w-[260px]">Routes to site</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allKeys.map((k) => (
                      <TableRow key={k} data-testid={`mapping-row-${k}`}>
                        <TableCell className="font-mono text-xs">{k}</TableCell>
                        <TableCell>
                          <Select value={draft[k] || ""} onValueChange={(v) => setMappingFor(k, v)}>
                            <SelectTrigger data-testid={`mapping-select-${k}`}>
                              <SelectValue placeholder="-- unmapped --" />
                            </SelectTrigger>
                            <SelectContent>
                              {(data.available_sites || []).map((code) => (
                                <SelectItem key={code} value={code}>
                                  {SITE_LABEL[code] || code} <span className="text-gray-400 ml-1 font-mono">({code})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {draft[k] && (
                            <Button size="sm" variant="ghost" onClick={() => setMappingFor(k, "")} data-testid={`mapping-clear-${k}`}>
                              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Add a new mapping</div>
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full">
                  <Label htmlFor="new-mapping-key" className="text-xs">Xero key (id or name)</Label>
                  <Input
                    id="new-mapping-key"
                    placeholder="e.g. Wellington Holiday Group  or  abc-123-cal"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    className="font-mono text-xs"
                    data-testid="new-mapping-key"
                  />
                </div>
                <div className="w-full sm:w-[200px]">
                  <Label htmlFor="new-mapping-site" className="text-xs">Site</Label>
                  <Select value={newSiteValue} onValueChange={setNewSite}>
                    <SelectTrigger id="new-mapping-site" data-testid="new-mapping-site"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data.available_sites || []).map((code) => (
                        <SelectItem key={code} value={code}>{SITE_LABEL[code] || code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addRow} variant="outline" data-testid="add-mapping-row">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              <p className="text-[11px] text-gray-500">
                Keys are matched case-insensitively against any of: <code>HolidayGroupID</code>,{" "}
                <code>HolidayGroupName</code>, <code>PayrollCalendarID</code>, <code>PayrollCalendarName</code>,{" "}
                <code>LocationID</code>, <code>Location</code>. First hit wins.
              </p>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="fix-codes-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Realign employee codes</div>
                  <p className="text-xs text-amber-900/80 mt-1">
                    After a site move, some employees may have codes that no longer match their site
                    (e.g. <code>CHCH014</code> sitting at Wellington). Scan and reassign codes so the
                    prefix follows the site.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => runFix(true)}
                    disabled={fixBusy}
                    data-testid="fix-codes-preview"
                    className="border-amber-300"
                  >
                    {fixBusy ? "Scanning..." : "Preview"}
                  </Button>
                  <Button
                    onClick={() => runFix(false)}
                    disabled={fixBusy}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="fix-codes-run"
                  >
                    Reassign codes
                  </Button>
                </div>
              </div>
              {fixPreview && (
                <div className="text-xs bg-white border border-amber-200 rounded p-2">
                  <div className="font-semibold text-amber-900 mb-1">
                    Preview · {fixPreview.fixed_count} reassignment{fixPreview.fixed_count === 1 ? "" : "s"}
                  </div>
                  {fixPreview.fixed_count === 0 ? (
                    <div className="text-gray-500">All codes already align with their sites — nothing to do.</div>
                  ) : (
                    <ul className="space-y-0.5 font-mono text-[11px]">
                      {fixPreview.fixed.slice(0, 50).map((f) => (
                        <li key={f.id}>
                          <span className="text-gray-700">{f.name}</span>{" — "}
                          <span className="text-gray-500">{f.old_code}</span>{" → "}
                          <span className="text-amber-700 font-semibold">{f.new_code}</span>
                          <span className="text-gray-400 ml-2">({f.site})</span>
                        </li>
                      ))}
                      {fixPreview.fixed.length > 50 && (
                        <li className="text-gray-500">…and {fixPreview.fixed.length - 50} more</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
